import React, { useContext } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { View, Text, Pressable } from 'react-native';
import styled from 'styled-components/native';
import DataTable from '@preflighttech/preflight-tables';
import moment from 'moment';
import FileDownload from 'js-file-download';

import AuthContext from 'app/src/contexts/AuthContext';
import DataState from 'app/src/components/DataState';
import ResourceNotifications from 'app/src/components/Notifications/Resource';
import minimalTable from 'app/src/elements/minimalTable';
import { Link } from 'app/src/utils/routing';
import currency from 'app/src/utils/currencyFormatter';

import { rest } from 'app/src/utils/rest';


const date = value => {
  if (!value) { return ''; }

  return moment(value).format('M/D/YY');
};

// Not defining in ./queries to avoid imports loop
const ASSET_TITLE_LOGS = gql`
  query AssetTitleLogs(
    $id: ID!
  ) {
    asset(id: $id) {
      id
      year
      make
      model
      name
      vin
      titleLogs {
        id

        upload { id, filename, contentType, url}
        linkedAttachment { id, name, url }
        titleNumber
        state
        notes
        status
        latestUpload { url }
        titleLogHistoryDeltas {
          id
          userDescription
          createdAt
          createdTimestamp
          resourceType
          resourceId
          fields {
            name
            oldValue
            newValue
          }
        }
        titleLogHistoryComments {
          id
          userDescription
          createdAt
          createdTimestamp
          body
        }
      }
    }
  }
`;

const TitleLogContainer = styled(View)`
  margin: 10px;
`;

const TitleLogDateTime = styled(Text)`
  font-weight: 600;
`;

const Offers = styled(View)`
  margin: 5px;
  padding: 5px;
  background-color: white;
  border-width: 1px;
  border-color: blank;
`;

const Field = styled(View)`
  flex-direction: row;
  gap: 10px;
`;

const Label = styled(Text)`
  min-width: 100px;
`;

const columns = [
  {
    key: 'date',
    label: 'Date',
    sort: 'prevent',
    width: 90,
    content: ({ entry }) => date(entry.createdAt),
  },
  {
    key: 'time',
    label: 'Time',
    sort: 'prevent',
    width: 90,
    content: ({ entry }) => moment(entry.createdAt).format('h:mm A'),
  },
  {
    key: 'userDescription',
    label: 'User',
    sort: 'prevent',
    width: "25%",
  },
  {
    key: 'deltaDescription',
    label: 'Action',
    sort: 'prevent',
  },
];

const deltaDescription = ({ fields }) => {

  let formattedStrings = [];

  if (fields.status) {
    formattedStrings.push(`Status: ${fields.status.newValue}`);
  }

  if(fields.state) {
    formattedStrings.push(`State: ${fields.state.newValue}`);
  }

  if(fields.titleNumber) {
    formattedStrings.push(`Title Number: ${fields.titleNumber.newValue}`);
  }

  if(fields.trackingNumber) {
    formattedStrings.push(`Tracking Number: ${fields.trackingNumber.newValue}`);
  }

  if(fields.trackingShipper) {
    formattedStrings.push(`Carrier: ${fields.trackingShipper.newValue}`);
  }

  if(fields.notes) {
    formattedStrings.push(`Notes: ${fields.notes.newValue}`);
  }

  if(fields.upload) {
    formattedStrings.push(
      <Link to={entry.upload.url} target='_blank'>
        <Text>
          {titleLog.upload.filename}
        </Text>
      </Link>
    )
  }

  if(fields.linkedAttachment) {
    formattedStrings.push(
      <Link to={entry.linkedAttachment?.url} target='_blank'>
        <Text>
          {`${titleLog.linkedAttachment?.name} (linked)`}
        </Text>
      </Link>
    )
  }

  return formattedStrings.join(', ');
}

const TitleLog = ({ asset, titleLog, refetch }) => {
  const { user, credentials, hasPolicy } = useContext(AuthContext);

  const deltas = [];

  titleLog.titleLogHistoryDeltas.forEach(delta => {

    const { fields, ...deltaObject } = delta;
    deltaObject.fields = {};

    fields.forEach(field => (
      deltaObject.fields[field.name] = field
    ));

    deltaObject.deltaDescription = deltaDescription(deltaObject, titleLog);

    if (deltaObject.deltaDescription) {
      deltas.push(deltaObject);
    }
  });

  titleLog.titleLogHistoryComments.forEach(comment => {
    const { body, ...deltaObject } = comment;
    deltaObject.deltaDescription = `Comment: ${body}`;
    deltas.push(deltaObject);
  });

  const sortedDeltas = deltas
    .sort((a, b) => a.createdTimestamp - b.createdTimestamp);
  return (
    <TitleLogContainer>
      <Field>
        <TitleLogDateTime>
          {`Created Date: ${date(titleLog.titleLogHistoryDeltas[0].createdAt)}`}
        </TitleLogDateTime>
        <ResourceNotifications resource={titleLog} />
      </Field>
      {deltas.length > 0 && (
        <DataTable.Simple
          data={sortedDeltas}
          columns={columns}
          component={minimalTable}
          pageLength="All"
        />
      )}
    </TitleLogContainer>
  );
};

const TitleLogHistory = ({ asset }) => {
  const { data, loading, error, refetch } = useQuery(ASSET_TITLE_LOGS, {
    variables: { id: asset.id }, fetchPolicy: 'no-cache',
  });

  if (!data) { return <DataState.Loading /> }

  const titleLogs = data.asset.titleLogs;

  if (0 === titleLogs.length) {
    return (<Text>No Title Logs Created</Text>);
  }

  return (
    <View>
      {titleLogs.map(titleLog => (
        <TitleLog
          key={titleLog.id}
          titleLog={titleLog}
          asset={data.asset}
          refetch={refetch}
        />
      ))}
    </View>
  );
};

export default TitleLogHistory;
